import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';

import { type AddVoucherTogglePayload, addVoucherToggle } from '~/services/PromoService';

import { promoByToggleQueryKeys } from './useSearchPromosByToggle';

type AddVoucherToggleParams = Omit<AddVoucherTogglePayload, 'brand'>;

const voucherToggleBrands = new Set([
  'luxuryescapes',
  'cudo',
  'deals',
  'treatme',
  'scooponexperience',
  'scoopontravel',
  'cudotravel',
  'dealstravel',
  'treatmetravel',
  'kogantravel',
  'lebusinesstraveller',
  'zoomzoom',
  'leagenthub',
  'leagenthub_cvfr',
]);

function isVoucherToggleBrand(brand: string): brand is AddVoucherTogglePayload['brand'] {
  return voucherToggleBrands.has(brand);
}

/** Adds a single toggle atomically, retaining every existing voucher toggle. */
export function useAddVoucherToggle() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { promoBrand } = useCurrentPromoBrand();

  return useMutation({
    mutationFn: async (params: AddVoucherToggleParams) => {
      if (!isVoucherToggleBrand(promoBrand)) {
        throw new Error(`Voucher toggle repairs are not supported for the ${promoBrand} promo brand.`);
      }

      return await addVoucherToggle({
        ...params,
        brand: promoBrand,
      });
    },
    onSuccess: (response) => {
      enqueueSnackbar(response.message, { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: promoByToggleQueryKeys.all });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Unable to repair voucher toggle: ${error.message}`, { variant: 'error' });
    },
  });
}
